import { FC, useState, useEffect, forwardRef, useRef } from 'react';
import { AllieSelectColorProps, AllieSelectProps } from './Select.types';
import { StyledSelectOption } from './Select.styles';
import { StyledSelect } from './Select.styles';
import { AllieNote } from '../Item/Note';
import { AllieCloseCircleIcon } from '../../styling/icons';
import { isMobile } from '../../common/utils';
import './Select.css';

const popoverOptions = {
  arrow: false,
  size: 'cover',
};

const actionSheetOptions = {
  cssClass: 'allie-select-sheet',
};

const interfaceProps = {
  popover: popoverOptions,
  ['action-sheet']: actionSheetOptions,
};

const getPropsInterface = function (interfaceSelected: string, opts: any): any {
  const interfaceOptions = interfaceProps[interfaceSelected as keyof typeof interfaceProps];
  return {
    interface: interfaceSelected,
    ...(interfaceOptions ? { interfaceOptions: { ...interfaceOptions, ...opts } } : {}),
  };
};

const errorStyle: AllieSelectColorProps = {
  backgroundColor: 'error-20',
  allieColor: 'neutral-60',
};

const interfaceDefault = isMobile ? 'action-sheet' : 'popover';

export const AllieSelect: FC<AllieSelectProps> = forwardRef(
  (
    {
      interface: interfaceSelected = interfaceDefault,
      color,
      options,
      onChange,
      selected,
      type,
      className,
      allieType,
      errorMessage,
      interfaceOptions,
      ...props
    },
    ref: any
  ) => {
    const defaultColor = {
      backgroundColor: color?.backgroundColor || 'white',
      allieColor: color?.allieColor || 'neutral-50',
    };
    const [styleItem, setStyleItem] = useState<AllieSelectColorProps>(defaultColor);
    const justMounted = useRef(true);

    const [selectedValue, setSelectedValue] = useState(selected);

    const setStyleOptionSelected = (optionSelected: any) => {
      if (optionSelected && optionSelected.color) {
        setStyleItem(optionSelected.color);
      } else {
        setStyleItem(defaultColor);
      }
    };

    useEffect(() => {
      setStyleOptionSelected(options.find((op) => op.value === selected));
    }, []);

    useEffect(() => {
      setSelectedValue(selected);
    }, [selected]);

    useEffect(() => {
      if (allieType === 'error') {
        setStyleItem(errorStyle);
      } else if (selected) {
        setStyleOptionSelected(options.find((op) => op.value === selected));
      } else {
        setStyleItem(defaultColor);
      }
    }, [allieType]);

    useEffect(() => {
      justMounted.current = false;
    }, []);

    const handleChange = (e: any) => {
      if (onChange && !justMounted.current) {
        setStyleOptionSelected(options.find((op) => op.value === e.detail.value));
        setSelectedValue(e.detail.value);
        if (e.target.getAttribute('programatic') !== 'true') {
          onChange(e);
        }
      }
    };

    return (
      <>
        <StyledSelect
          {...getPropsInterface(interfaceSelected, interfaceOptions)}
          {...props}
          allieType={allieType}
          color={styleItem}
          type={type}
          onIonChange={handleChange}
          className={`allie-text-button-normal ${className}`}
          value={selectedValue}
          mode="md"
          ref={ref}
        >
          {options &&
            options.map(({ text, ...propsOptions }) => (
              <StyledSelectOption
                key={`${propsOptions.value}-${text}`}
                className="allie-text-body3"
                {...propsOptions}
              >
                {text}
              </StyledSelectOption>
            ))}
        </StyledSelect>
        {errorMessage && (
          <AllieNote className="allie-text-field3" slot="error">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '10px',
              }}
            >
              <AllieCloseCircleIcon color="error" />
              {errorMessage}
            </div>
          </AllieNote>
        )}
      </>
    );
  }
);
